import * as React from 'react';
import {
  Button,
  Combobox,
  Option,
  Label,
  Badge,
  makeStyles,
  tokens,
  useId,
} from '@fluentui/react-components';
import type { ComboboxProps } from '@fluentui/react-components';
import { Filter20Regular, Dismiss12Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: '220px',
    maxWidth: '400px',
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    color: tokens.colorNeutralForeground3,
  },
  selectedFilters: {
    listStyleType: 'none',
    margin: 0,
    marginBottom: tokens.spacingVerticalXXS,
    paddingLeft: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXXS,
  },
});

interface MultiselectFilterOption {
  value: string;
  label: string;
}

interface MultiselectFilterProps {
  label: string;
  placeholder: string;
  options: MultiselectFilterOption[];
  selectedOptions: string[];
  onSelectionChange: (selectedOptions: string[]) => void;
}

export const MultiselectFilter: React.FC<MultiselectFilterProps> = ({
  label,
  placeholder,
  options,
  selectedOptions,
  onSelectionChange,
}) => {
  const styles = useStyles();
  const comboId = useId('combo-filter');
  const listRef = React.useRef<HTMLUListElement>(null);
  const comboInputRef = React.useRef<HTMLInputElement>(null);

  const handleRemoveOption = (optionValue: string, index: number) => {
    onSelectionChange(selectedOptions.filter(id => id !== optionValue));
    
    // Gerenciar foco após remoção
    const indexToFocus = index === 0 ? 1 : index - 1;
    const optionToFocus = listRef.current?.querySelector(
      `#${comboId}-remove-${indexToFocus}`
    );
    if (optionToFocus) {
      (optionToFocus as HTMLButtonElement).focus();
    } else {
      comboInputRef.current?.focus();
    }
  };

  const onSelect: ComboboxProps['onOptionSelect'] = (event, data) => {
    onSelectionChange(data.selectedOptions);
  };

  return (
    <div className={styles.filterField}>
      <div className={styles.filterLabel}>
        <Filter20Regular />
        <Label htmlFor={comboId} size="small">{label}</Label>
        {selectedOptions.length > 0 && (
          <Badge appearance="tint" color="brand" size="small">
            {selectedOptions.length}
          </Badge>
        )}
      </div>
      {selectedOptions.length > 0 && (
        <ul className={styles.selectedFilters} ref={listRef}>
          <span id={`${comboId}-remove`} hidden>
            Remover
          </span>
          {selectedOptions.map((optionValue, i) => {
            const option = options.find(o => o.value === optionValue);
            return option ? (
              <li key={optionValue}>
                <Button
                  size="small"
                  shape="circular"
                  appearance="primary"
                  icon={<Dismiss12Regular />}
                  iconPosition="after"
                  onClick={() => handleRemoveOption(optionValue, i)}
                  id={`${comboId}-remove-${i}`}
                  aria-labelledby={`${comboId}-remove ${comboId}-remove-${i}`}
                >
                  {option.label}
                </Button>
              </li>
            ) : null;
          })}
        </ul>
      )}
      <Combobox
        id={comboId}
        multiselect
        placeholder={placeholder}
        size="medium"
        selectedOptions={selectedOptions}
        onOptionSelect={onSelect}
        ref={comboInputRef}
      >
        {options.map(option => (
          <Option key={option.value} value={option.value} text={option.label}>
            {option.label}
          </Option>
        ))}
      </Combobox>
    </div>
  );
};
